/**
 * WorkspacePricingCatalog
 *
 * Single source of truth for the workspace plan + add-on pricing *fallbacks*
 * displayed in the frontend when the backend /api/v1/billing/plans endpoint
 * is unreachable or still in-flight.
 *
 * At runtime the authoritative pricing comes from Stripe via the backend
 * catalog (see StripeConfig.fetchPricingCatalog()). This module exists so
 * that every billing surface shows the SAME fallback numbers — preventing
 * the "$49 in one modal, $99.99 in another" drift that customers noticed
 * when the backend fetch failed.
 *
 * Canonical prices (matches docs/WORKSPACE_SUBSCRIPTION_MODEL.md and
 * docs/PRICING_ANALYSIS.md):
 *   Starter: $9.99/mo
 *   Pro:     $24.99/mo
 *   Team:    $99.99/mo
 *
 * Touch THIS file — and only this file — when marketing/finance changes
 * the published plan prices.
 */

// --- Plan pricing (dollars, matches Stripe monthly/yearly amounts) ----------
// Yearly totals are monthly * 12 with a 20% discount rounded to end in .88:
//   starter: 9.99 * 12 * 0.8  -> 95.88
//   pro:     24.99 * 12 * 0.8 -> 239.88
//   team:    99.99 * 12 * 0.8 -> 959.88
// Backend returns these as cents (9999, 23988, 95988, ...). The backend is
// authoritative — these fallbacks exist purely for the offline/error path.
var FALLBACK_PLAN_PRICES = Object.freeze({
    free:    { monthly: 0,     yearly: 0,      currency: 'usd' },
    starter: { monthly: 9.99,  yearly: 95.88,  currency: 'usd' },
    pro:     { monthly: 24.99, yearly: 239.88, currency: 'usd' },
    team:    { monthly: 99.99, yearly: 959.88, currency: 'usd' }
});

// --- Plan features / limits (mirrors WorkspaceRulesHelper.PLAN_LIMITS) ------
// Values are used when the backend response is missing fields (graceful
// degradation). `null` means "Unlimited" in UI copy.
var FALLBACK_PLAN_FEATURES = Object.freeze({
    free:    { hours: 0.5, storageGB: 1,   editors: 1, reviewersPerMonth: 1 },
    starter: { hours: 5,   storageGB: 10,  editors: 1, reviewersPerMonth: 3 },
    pro:     { hours: 20,  storageGB: 50,  editors: 2, reviewersPerMonth: null },
    team:    { hours: 100, storageGB: 200, editors: 5, reviewersPerMonth: null }
});

// --- Hour pack add-ons ------------------------------------------------------
// Standalone, one-time purchases (not recurring). Priced per pack, backend
// returns them as cents under /api/v1/billing/plans#addOns.hourPacks.
var FALLBACK_HOUR_PACKS = Object.freeze([
    Object.freeze({ packSize: 5,  price: 5.99,  currency: 'usd' }),
    Object.freeze({ packSize: 10, price: 9.99,  currency: 'usd' }),
    Object.freeze({ packSize: 20, price: 17.99, currency: 'usd' })
]);

// --- Plan ordering ----------------------------------------------------------
// Keeps Change Plan / Upgrade-to-Team modals in consistent visual order.
var PLAN_ORDER = Object.freeze(['free', 'starter', 'pro', 'team']);

// --- Display helpers --------------------------------------------------------
function formatCurrencyAmount(amountInDollars, currency) {
    var normalized = (currency || 'usd').toString().toLowerCase();
    var rounded = Math.round(Number(amountInDollars) * 100) / 100;
    var prefix = normalized === 'cad' ? 'CA$' : '$';
    return prefix + rounded.toFixed(2);
}

/**
 * Formats a plan's monthly price as a short marketing string, e.g. "$9.99".
 * No trailing period — callers add "/mo" or "/month" as needed.
 */
function formatPlanMonthlyPriceShort(planType) {
    var entry = FALLBACK_PLAN_PRICES[planType] || FALLBACK_PLAN_PRICES.free;
    return formatCurrencyAmount(entry.monthly, entry.currency);
}

module.exports = {
    FALLBACK_PLAN_PRICES: FALLBACK_PLAN_PRICES,
    FALLBACK_PLAN_FEATURES: FALLBACK_PLAN_FEATURES,
    FALLBACK_HOUR_PACKS: FALLBACK_HOUR_PACKS,
    PLAN_ORDER: PLAN_ORDER,
    formatCurrencyAmount: formatCurrencyAmount,
    formatPlanMonthlyPriceShort: formatPlanMonthlyPriceShort
};
module.exports.default = module.exports;
